import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { EmployeeSettingsService } from '../services/EmployeeSettingsService';
import { RecentMeeting } from '../interfaces/interface';
import { MEETING_CONSTANTS } from '../constants/EAHuddleConstants';
import { logger } from 'src/logger';

/**
 * Manages recent meetings with DynamoDB persistence and client-side pagination.
 *
 * Loads all meetings for the given alias on mount, then exposes a paginated
 * view along with CRUD operations (add / delete). Pagination is computed
 * client-side from the full list — no server-side paging needed because
 * the total is capped at {@link MEETING_CONSTANTS.MAX_RECENT_MEETINGS}.
 *
 * @param alias - Employee alias whose recent meetings to load
 * @returns Meeting list, pagination controls, and CRUD operations
 */
export const useRecentMeetings = (alias: string, pageSize?: number) => {
  const [allMeetings, setAllMeetings] = useState<RecentMeeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const isMounted = useRef(true);

  // Load recent meetings from DynamoDB on mount
  useEffect(() => {
    isMounted.current = true;

    const loadMeetings = async () => {
      if (!alias) return;
      try {
        const meetings = await EmployeeSettingsService.getRecentMeetings(alias);
        if (isMounted.current) {
          setAllMeetings(meetings);
        }
      } catch (error: unknown) {
        logger.error('Failed to load recent meetings from DynamoDB', { error, alias });
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    loadMeetings();

    return () => {
      isMounted.current = false;
    };
  }, [alias]);

  const PAGE_SIZE = pageSize ?? MEETING_CONSTANTS.PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(allMeetings.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);

  const visibleMeetings = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return allMeetings.slice(start, start + PAGE_SIZE);
  }, [allMeetings, safePage, PAGE_SIZE]);

  const totalMeetingCount = allMeetings.length;

  /** Navigate to a specific page — uses functional updater to avoid totalPages dependency */
  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  /**
   * Optimistically add a new recent meeting to the local list.
   * The actual DDB write (saveMeeting) is handled by the caller.
   */
  const addRecentMeeting = useCallback((meeting: RecentMeeting) => {
    setAllMeetings((prev) => [meeting, ...prev.filter((m) => m.meetingId !== meeting.meetingId)]);
  }, []);

  /** Update the subject for a specific meeting in the local list (optimistic). */
  const updateMeetingSubject = useCallback((meetingId: string, subject: string) => {
    setAllMeetings((prev) => prev.map((m) => (m.meetingId === meetingId ? { ...m, subject } : m)));
  }, []);

  /**
   * Delete a meeting from DynamoDB and optimistically remove from local state.
   */
  const deleteRecentMeeting = useCallback(
    async (meetingId: string) => {
      if (!alias) return;

      // Optimistic removal
      setAllMeetings((prev) => prev.filter((m) => m.meetingId !== meetingId));

      try {
        await EmployeeSettingsService.deleteMeeting(alias, meetingId);
        logger.info('Meeting deleted successfully', { meetingId, alias });
      } catch (error: unknown) {
        logger.error('Failed to delete meeting from DynamoDB', { error, meetingId, alias });
        // Reload from DDB to restore correct state on failure
        try {
          const meetings = await EmployeeSettingsService.getRecentMeetings(alias);
          setAllMeetings(meetings);
        } catch {
          // Silent fallback — state may be stale
        }
      }
    },
    [alias]
  );

  const refreshMeetings = useCallback(async () => {
    if (!alias) return;
    try {
      const meetings = await EmployeeSettingsService.getRecentMeetings(alias);
      if (isMounted.current) setAllMeetings(meetings);
    } catch (error) {
      logger.error('Failed to refresh recent meetings', { error, alias });
    }
  }, [alias]);

  return {
    recentMeetings: visibleMeetings,
    allRecentMeetings: allMeetings,
    totalMeetingCount,
    currentPage: safePage,
    totalPages,
    goToPage,
    isLoadingRecentMeetings: isLoading,
    addRecentMeeting,
    updateMeetingSubject,
    deleteRecentMeeting,
    refreshMeetings
  };
};
